import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ImageUp, Save, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AppMark } from '@/components/layout/app-switcher';
import { ConfirmButton } from '@/components/shared/confirm-button';
import { CopyButton } from '@/components/shared/copy-button';
import { DeleteAppDialog } from '@/components/shared/delete-app-dialog';
import { FormField } from '@/components/shared/form-field';
import { LoadingButton } from '@/components/shared/loading-button';
import { SectionCard } from '@/components/shared/section-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { LOGO_ACCEPT, LOGO_SIZE, logoFromFile } from '@/lib/logo';
import { notify, notifyError } from '@/lib/toast';
import { urlProblem } from '@/lib/url';
import type { App, AppDetail } from '@/lib/types';
import { useAppCtx } from './app-layout';

type Patch = Partial<Pick<App, 'name' | 'description' | 'url' | 'logo'>>;

export function Settings() {
  const { app } = useAppCtx();
  // Keyed so switching to another application starts from that app's values.
  return <SettingsForm key={app.id} app={app} />;
}

function useUpdateApp(appId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Patch) => api<App>(`/admin/v1/apps/${appId}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['app', appId] });
      void qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

function SettingsForm({ app }: { app: AppDetail }) {
  const [name, setName] = useState(app.name);
  const [description, setDescription] = useState(app.description ?? '');
  const [url, setUrl] = useState(app.url ?? '');
  const general = useUpdateApp(app.id);

  const changes: Patch = {};
  if (name.trim() !== app.name) changes.name = name.trim();
  if (description !== (app.description ?? '')) changes.description = description;
  if (url.trim() !== (app.url ?? '')) changes.url = url.trim() || null;
  const dirty = Object.keys(changes).length > 0;
  const urlError = urlProblem(url.trim());

  const save = () => general.mutate(changes, { onSuccess: () => notify('Settings saved') });

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
      <div className="grid h-fit gap-6">
        <SectionCard title="General" description="How this application appears in the dashboard.">
          <form className="grid gap-5" onSubmit={(e) => { e.preventDefault(); if (dirty && !urlError && name.trim()) save(); }}>
            <FormField label="Name" htmlFor="app-name" error={name.trim() ? undefined : 'Name is required.'}>
              <Input id="app-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="My Mobile App" />
            </FormField>
            <FormField label="Application URL" htmlFor="app-url" hint="The application's own website, e.g. https://myshop.com. Optional." error={urlError ?? undefined}>
              <Input id="app-url" type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://myshop.com" maxLength={2048} />
            </FormField>
            <FormField label="Description" htmlFor="app-description">
              <Textarea id="app-description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} placeholder="What this app is and who uses it" />
            </FormField>
            {/* items-start: Slug's hint makes its column taller; stretching would push the ID field down. */}
            <div className="grid items-start gap-3 sm:grid-cols-2">
              <FormField label="Slug" hint="Set when the app was created; it can't be changed.">
                <Input value={app.slug} readOnly disabled className="font-mono text-xs" />
              </FormField>
              <FormField label="Application ID">
                {/* Copy sits inside the field so it lines up with Slug beside it. */}
                <div className="relative">
                  <Input value={app.id} readOnly disabled className="pr-10 font-mono text-xs" />
                  <div className="absolute inset-y-0 right-1.5 flex items-center"><CopyButton value={app.id} label={false} /></div>
                </div>
              </FormField>
            </div>
            <div className="flex justify-end">
              <LoadingButton type="submit" loading={general.isPending} disabled={!dirty || !!urlError || !name.trim()}><Save /> Save changes</LoadingButton>
            </div>
          </form>
        </SectionCard>
        <DangerZone app={app} />
      </div>

      <LogoCard app={app} />
    </div>
  );
}

function DangerZone({ app }: { app: App }) {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<App | null>(null);
  return (
    <SectionCard>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="grid max-w-xl gap-1">
          <p className="text-sm font-medium">Delete application</p>
          <p className="text-sm text-muted-foreground">Stops every notification for {app.name} and disables its API keys.</p>
        </div>
        {/* Quiet until hovered: the confirm dialog carries the real warning, so the card needn't shout. */}
        <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleting(app)}>
          <Trash2 /> Delete application
        </Button>
      </div>
      <DeleteAppDialog app={deleting} onClose={() => setDeleting(null)} onDeleted={() => navigate('/', { replace: true })} />
    </SectionCard>
  );
}

function LogoCard({ app }: { app: App }) {
  const input = useRef<HTMLInputElement>(null);
  const [confirming, setConfirming] = useState(false);
  const update = useUpdateApp(app.id);

  const upload = async (file: File) => {
    try {
      const logo = await logoFromFile(file);
      update.mutate({ logo }, { onSuccess: () => notify('Logo updated') });
    } catch (e) {
      notifyError(e);
    }
  };

  return (
    <SectionCard title="Logo" description="Shown in the application list, the app switcher and this page's header." className="h-fit">
      <div className="grid gap-4">
        <div className="flex items-center gap-4">
          <AppMark name={app.name} logo={app.logo} className="size-20 text-3xl" />
          <p className="text-xs text-muted-foreground">PNG, JPG, WebP, GIF or SVG. Resized to {LOGO_SIZE}×{LOGO_SIZE}; a square image looks best.</p>
        </div>
        <input
          ref={input}
          type="file"
          accept={LOGO_ACCEPT}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }}
        />
        <div className="flex flex-wrap gap-2">
          <LoadingButton variant="outline" size="sm" loading={update.isPending} onClick={() => input.current?.click()}>
            <ImageUp /> {app.logo ? 'Replace logo' : 'Upload logo'}
          </LoadingButton>
          {app.logo && (
            <ConfirmButton
              label="Remove"
              confirmLabel="Remove logo"
              confirming={confirming}
              pending={update.isPending}
              onAsk={() => setConfirming(true)}
              onCancel={() => setConfirming(false)}
              onConfirm={() => update.mutate({ logo: null }, { onSuccess: () => { setConfirming(false); notify('Logo removed'); } })}
            />
          )}
        </div>
      </div>
    </SectionCard>
  );
}
